import { useEffect, useState } from 'react';
const DEFAULT_HEADER_HEIGHT = 72;
const SELECTOR_HEADER_FIXED = '[data-testid="header-fixed-container"]';
const SELECTOR_HEADER_CONTENT = '[data-testid="header-container"]';
const SELECTOR_BANNER = '[data-testid="smart-action-banner-staleCV"]';
export const useHeaderOffset = () => {
    const [headerOffset, setHeaderOffset] = useState(DEFAULT_HEADER_HEIGHT);
    useEffect(() => {
        const resizeObservers = [];
        // querySelector is intentional: the header is rendered by a separate microfrontend
        // and is not part of this React tree, so a ref is not possible.
        const header = document.querySelector(SELECTOR_HEADER_FIXED);
        const recalcOffset = () => {
            const banner = document.querySelector(SELECTOR_BANNER);
            // header-fixed-container is an empty sentinel: present = fixed header mode,
            // absent = header scrolls with the page (e.g. small viewports).
            // Do not check getComputedStyle — the microfrontend CSS may load after
            // this runs, making position appear 'static' even when it will be 'fixed'.
            if (!header) {
                setHeaderOffset(0);
                return;
            }
            // header-fixed-container is empty — measure height from the sibling
            // header-container which holds the actual header content.
            // Fall back to DEFAULT_HEADER_HEIGHT if the element isn't measurable yet.
            const headerContent = document.querySelector(SELECTOR_HEADER_CONTENT);
            const headerHeight = headerContent?.getBoundingClientRect().height || DEFAULT_HEADER_HEIGHT;
            const bannerHeight = banner ? banner.getBoundingClientRect().height : 0;
            setHeaderOffset(headerHeight + bannerHeight);
        };
        recalcOffset();
        // Re-check whenever the header resizes (covers fixed→relative transitions)
        if (header) {
            const obs = new ResizeObserver(recalcOffset);
            obs.observe(header);
            resizeObservers.push(obs);
        }
        let observedBanner = null;
        const mutationObserver = new MutationObserver(() => {
            recalcOffset();
            const banner = document.querySelector(SELECTOR_BANNER);
            if (banner && banner !== observedBanner) {
                observedBanner = banner;
                const obs = new ResizeObserver(recalcOffset);
                obs.observe(banner);
                resizeObservers.push(obs);
            }
        });
        // Observe the microfrontend root (header's parent) rather than document.body —
        // the banner is a sibling of the header, so this scope is sufficient and far less noisy.
        const mutationRoot = header?.parentElement ?? document.body;
        mutationObserver.observe(mutationRoot, { childList: true, subtree: true });
        return () => {
            resizeObservers.forEach((obs) => obs.disconnect());
            mutationObserver.disconnect();
        };
    }, []);
    return headerOffset;
};
